import { useMemo } from 'react';
import { useAgendaColumnInfiniteQuery } from './useAgendaColumnInfiniteQuery';

/**
 * SIMPLIFIED infinite scroll hook for Agenda View - SINGLE CACHE
 *
 * Uses React Query's infinite query directly - ALL pages in one cache.
 * Wraps useAgendaColumnInfiniteQuery and provides flattened data.
 *
 * Benefits:
 * - Single cache per column
 * - All data in one place
 * - Built-in pagination state
 *
 * @param {string} columnId - The column/stage ID
 * @param {Object} filters - Filter parameters
 * @returns {Object} - Flattened items, pagination controls, and loading states
 */
export const useAgendaColumnInfiniteData = (columnId, filters = {}, methodType = 'GET', currentView = 'agenda') => {
  const infiniteQuery = useAgendaColumnInfiniteQuery(columnId, filters, methodType, currentView);

  // Flatten all pages into single array
  const allItems = useMemo(() => {
    return infiniteQuery.data?.pages.flatMap((page) => page.items) || [];
  }, [infiniteQuery.data]);

  // Get total count from first page
  const totalCount = useMemo(() => {
    return infiniteQuery.data?.pages[0]?.totalCount || 0;
  }, [infiniteQuery.data]);

  const isActuallyLoading = infiniteQuery.isLoading && infiniteQuery.isFetching;

  return {
    items: allItems,
    totalCount,
    pages: infiniteQuery.data?.pages || [], // Expose pages for page-based access
    fetchNextPage: infiniteQuery.fetchNextPage,
    fetchPage: infiniteQuery.fetchPage, // Add direct page fetch function
    hasNextPage: infiniteQuery.hasNextPage,
    isFetchingNextPage: infiniteQuery.isFetchingNextPage,
    isLoading: isActuallyLoading, // Only show loading if actively fetching
    isError: infiniteQuery.isError,
    error: infiniteQuery.error,
  };
};
